import { useEffect, useRef, useState } from "react";
import styles from "./MicroModalSelector.module.scss";
import { ArrowDown } from "./assets/icon";
import Loader from "../../molecule/Loader/Loader";
import { IconType, JWayIcon } from "../../../assets/JWayIcons";
import useOnScreen from "../../../../utils/useOnScreen";
import { motion, AnimatePresence } from "framer-motion";

export interface ResultItem {
  id: string;
  name: string;
  item: any;
}

export type ViewRenderParams = {
  currentValue: any;
  isOpenModal: boolean;
  openModal: () => void;
  closeModal: () => void;
}

export  type CustomViewPickerRender = (params: ViewRenderParams) => JSX.Element;
export interface MicroModalSelectorSearchProps {
  valueSelectedName: string | null | undefined;
  placeholder?: string;
  setQuery: (query: string) => void;
  valueQuery?: string | null;
  result: ResultItem[];
  variant?: "dark" | "light";
  onClickItem: (item: any) => void;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  label?: string;
  valueRenderer?: () => JSX.Element;
  iconType?: IconType;
  createAlwaysDefaultItem?: boolean;
  createDefaultItem?: (value: string) => any;
  itemRendererSearch?: (item: {
    item: any;
    onClick: () => void;
  }) => JSX.Element;
  searchPlaceholder?: string;
  setPage?: (page: number) => void;
  lastPage?: number;
  currentPage?: number;
  isLoadingMore?: boolean;
  style?:  React.CSSProperties;
  customViewRender?: (params: ViewRenderParams ) => JSX.Element;
}

export default function MicroModalSelectorSearch({
  valueSelectedName,
  placeholder,
  result,
  setQuery,
  variant = "light",
  onClickItem,
  isLoading,
  valueQuery,
  label,
  isError,
  itemRendererSearch,
  valueRenderer,
  createDefaultItem,
  iconType,
  createAlwaysDefaultItem,
  searchPlaceholder,
  setPage,
  lastPage,
  currentPage,
  isLoadingMore,
  customViewRender,
  style,
}: MicroModalSelectorSearchProps) {
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setShowModal(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      setQuery("");
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const withDefaultItem = (resultRaw: ResultItem[]) => {
    if (
      createDefaultItem &&
      valueQuery &&
      ((valueQuery.length > 0 && result.length === 0) ||
        createAlwaysDefaultItem)
    ) {
      return [
        {
          id: valueQuery,
          name: valueQuery,
          item: createDefaultItem(valueQuery),
        },
        ...resultRaw,
      ];
    } else {
      return resultRaw;
    }
  };
  return (
    <div style={{ position: "relative", width: style?.width}}  ref={modalRef}>
      {customViewRender &&
        customViewRender({
          currentValue: valueSelectedName,
          isOpenModal: showModal,
          openModal: () => setShowModal(true),
          closeModal: () => setShowModal(false),
        })}
        {!customViewRender &&
      <div
        className={[
          styles["global-container"],
          styles["global-container--" + variant],
        ].join(" ")}
      >
        <label
          className={[styles["label"], styles["label--" + variant]].join(" ")}
        >
          {label}
        </label>

        <div
          onClick={() => setShowModal(!showModal)}
          style={style}
          className={[
            styles["placeholder-container"],
            styles["placeholder-container--" + variant],
            styles[
              "placeholder-container--" +
                variant +
                "--" +
                (isError ? "error" : "")
            ],
          ].join(" ")}
        >
          {valueRenderer ? (
            valueRenderer()
          ) : (
            <p
              className={[
                styles["placeholder-container__label"],
                styles["placeholder-container__label--" + variant],
                valueSelectedName ? styles["placeholder-container__label--value"] : styles["placeholder-container__label--placeholder"],
              ].join(" ")}
            >
              {valueSelectedName || placeholder}
            </p>
          )}
          <div
            className={[
              styles["placeholder-container__icon"],
              styles["placeholder-container__icon--" + variant],
              showModal ? styles["placeholder-container__icon--active"] : "",
            ].join(" ")}
          >
            <ArrowDown
              size={10}
              color={variant === "dark" ? "white" : "gray"}
            />
          </div>
        </div>
        

      </div>
      
}
{showModal && (
          <Modal
            result={withDefaultItem(result)}
            onClickItem={onClickItem}
            variant={variant}
            setQuery={setQuery}
            valueQuery={valueQuery}
            handleCloseModal={() => setShowModal(false)}
            isLoading={isLoading}
            itemRendererSearch={itemRendererSearch}
            searchPlaceholder={searchPlaceholder}
            iconType={iconType}
            currentPage={currentPage}
            lastPage={lastPage}
            setPage={setPage}
            isLoadingMore={isLoadingMore}
          />
        )}
    </div>
  );
}

const Modal = ({
  result,
  onClickItem,
  variant,
  setQuery,
  valueQuery,
  isLoading,
  handleCloseModal,
  itemRendererSearch,
  searchPlaceholder,

  iconType,
  setPage,
  lastPage,
  currentPage,
  isLoadingMore,
}: {
  result: ResultItem[];
  valueQuery?: string | null;
  setQuery: (s: string) => void;
  onClickItem: (item: any) => void;
  variant: "dark" | "light";
  isLoading?: boolean;
  handleCloseModal?: () => void;
  searchPlaceholder?: string;
  itemRendererSearch?: (item: {
    item: any;
    onClick: () => void;
  }) => JSX.Element;

  iconType?: IconType;
  setPage?: (page: number) => void;
  lastPage?: number;
  currentPage?: number;
  isLoadingMore?: boolean;
}) => {
  const [ref, isIntersecting] = useOnScreen({ threshold: 0.5 });

  const handleLoadMore = () => {
    if (
      isIntersecting &&
      currentPage &&
      lastPage &&
      currentPage < lastPage &&
      !isLoadingMore
    ) {
      setPage && setPage(currentPage + 1);
    }
  };
  useEffect(() => {
    if (isIntersecting) {
      handleLoadMore();
    }
  }, [isIntersecting, result]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={[
        styles["modal-container"],
        styles["modal-container--" + variant],
      ].join(" ")}
    >
      <div
        className={[
          styles["modal-container__search"],
          styles["modal-container__search--" + variant],
        ].join(" ")}
      >
        <input
          className={[
            styles["modal-container__search__input"],
            styles["modal-container__search__input--" + variant],
          ].join(" ")}
          type="text"
          placeholder={searchPlaceholder || "Rechercher"}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          value={valueQuery || ""}
        />
        <div
          className={[styles["border"], styles["border--" + variant]].join(" ")}
        />
      </div>
      <div
        className={[
          styles["modal-container__result"],
          styles["modal-container__result--" + variant],
        ].join(" ")}
      >
        {isLoading && (
          <Loader centered color={variant === "dark" ? "#FFF" : ""} />
        )}
        {!isLoading &&
          result &&
          result.length !== 0 &&
          result.map((item) =>
            itemRendererSearch ? (
              itemRendererSearch({
                item: item.item,
                onClick: () => {
                  onClickItem(item.item);
                  handleCloseModal && handleCloseModal();
                },
              })
            ) : (
              <div
                key={item.id}
                className={[
                  styles["modal-item"],
                  styles["modal-item--" + variant],
                ].join(" ")}
                onClick={() => {
                  onClickItem(item);
                  handleCloseModal && handleCloseModal();
                }}
              >
                {iconType && (
                  <div style={{ marginRight: "8px" }}>
                    <JWayIcon size={13} color={"#929292"} type={iconType} />
                  </div>
                )}
                <p
                  className={[
                    styles["modal-item__name"],
                    styles["modal-item__name--" + variant],
                  ].join(" ")}
                >
                  {item.name}
                </p>
              </div>
            )
          )}
        {!isLoading && result && result.length === 0 && (
          <p
            className={[
              styles["modal-container__empty"],
              styles["modal-container__empty--" + variant],
            ].join(" ")}
          >
            Aucun résultat
          </p>
        )}
        <AnimatePresence mode="wait">
          {isLoadingMore ? (
            <motion.div
              className={[
                styles["loader-more"],
                styles["loader-more--" + variant],
              ].join(" ")}
              initial={{ transform: "scale(0.7)", opacity: 0 }}
              animate={{ transform: "scale(1)", opacity: 1 }}
              exit={{ transform: "scale(0.7)", opacity: 0 }}
              transition={{
                type: "spring",
                duration: 0.3,
                delay: 0.2,
                stiffness: 260,
                damping: 20,
              }}
            >
              <Loader
                size={30}
                paddingTop="0"
                centered
                color={variant === "dark" ? "#FFF" : ""}
              />
            </motion.div>
          ) : (
            <>
              <div></div>
              <div
              style={{ height: "50px", width: "100%", color: "transparent" }}
                ref={(ref as React.RefObject<HTMLDivElement>) || undefined}
                onClick={handleLoadMore}
              >end</div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
